import { Link, useParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumbs from "@/components/base/Breadcrumbs";
import RatingStars from "@/components/base/RatingStars";
import DramaCard from "@/components/base/DramaCard";
import { useSeo } from "@/hooks/useSeo";
import { dramas } from "@/mocks/dramas";
import { newsArticles } from "@/mocks/news";

const SITE_URL = "https://beesfly.cn";

function ratingDistribution(rating: number) {
  const top = Math.min(76, Math.round(rating * 8));
  const second = Math.round((100 - top) * 0.45);
  const third = Math.round((100 - top - second) * 0.5);
  const fourth = Math.round((100 - top - second - third) * 0.6);
  const fifth = Math.max(0, 100 - top - second - third - fourth);
  return [top, second, third, fourth, fifth];
}

export default function DramaDetailPage() {
  const { id } = useParams<{ id: string }>();
  const drama = dramas.find((item) => item.id === id);

  useSeo({
    title: drama
      ? `《${drama.title}》演员表与导演编剧信息 — 短剧爆料社`
      : "短剧未找到 — 短剧爆料社",
    description: drama
      ? `《${drama.title}》完整演员表、导演${drama.director}与编剧${drama.screenwriter}信息，附拍摄花絮、幕后制作故事与${drama.rating.toFixed(1)}分剧集口碑评分。`
      : "该短剧页面不存在，可返回短剧库浏览其他爆料短剧的演员表与导演编剧信息。",
    keywords: drama
      ? `${drama.title},${drama.title}演员表,${drama.director},${drama.screenwriter},拍摄花絮,幕后制作故事`
      : "爆料短剧,演员表查询",
    path: `/drama/${id ?? ""}`,
    image: drama?.cover,
    type: "video.tv_show",
    jsonLd: drama
      ? [
          {
            "@context": "https://schema.org",
            "@type": "TVSeries",
            name: drama.title,
            description: drama.synopsis,
            image: drama.cover,
            numberOfEpisodes: drama.episodes,
            datePublished: String(drama.year),
            genre: drama.tags,
            director: { "@type": "Person", name: drama.director },
            actor: drama.cast.map((item) => ({
              "@type": "Person",
              name: item.name,
              characterName: item.role,
            })),
            aggregateRating: {
              "@type": "AggregateRating",
              ratingValue: drama.rating,
              bestRating: 10,
              ratingCount: drama.ratingCount,
            },
          },
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "首页", item: `${SITE_URL}/` },
              { "@type": "ListItem", position: 2, name: "短剧库", item: `${SITE_URL}/dramas` },
              {
                "@type": "ListItem",
                position: 3,
                name: drama.title,
                item: `${SITE_URL}/drama/${drama.id}`,
              },
            ],
          },
        ]
      : undefined,
  });

  if (!drama) {
    return (
      <SiteLayout>
        <div className="mx-auto flex w-full max-w-content flex-col items-center px-4 py-24 text-center md:px-6">
          <span className="flex h-12 w-12 items-center justify-center rounded-full bg-background-100 text-foreground-500">
            <i className="ri-file-unknow-line text-2xl"></i>
          </span>
          <h1 className="mt-4 font-heading text-2xl font-bold text-foreground-950">没有找到这部短剧</h1>
          <p className="mt-3 text-sm text-foreground-600">
            它可能已被移除，或链接输入有误。你可以回到短剧库继续浏览。
          </p>
          <Link
            to="/dramas"
            className="mt-6 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm text-background-50 transition-colors hover:bg-primary-600"
          >
            返回短剧库
          </Link>
        </div>
      </SiteLayout>
    );
  }

  const distribution = ratingDistribution(drama.rating);
  const related = dramas
    .filter((item) => item.id !== drama.id && item.tags.some((tag) => drama.tags.includes(tag)))
    .slice(0, 4);
  const relatedNews = newsArticles
    .filter((article) => article.title.includes(drama.title))
    .slice(0, 3);

  return (
    <SiteLayout>
      <div className="border-b border-background-200 bg-background-100">
        <div className="mx-auto w-full max-w-content px-4 pt-8 md:px-6">
          <Breadcrumbs
            items={[
              { label: "首页", to: "/" },
              { label: "短剧库", to: "/dramas" },
              { label: drama.title },
            ]}
          />
        </div>

        <div className="mx-auto w-full max-w-content px-4 py-8 md:px-6 md:py-12">
          <div className="flex flex-col gap-8 lg:flex-row">
            <div className="w-full max-w-[280px] shrink-0 lg:w-72">
              <div className="overflow-hidden rounded-lg border border-background-200 bg-background-200">
                <div className="aspect-[3/4] w-full">
                  <img
                    src={drama.cover}
                    alt={`短剧《${drama.title}》官方海报`}
                    title={`${drama.title} 演员表 导演编剧信息`}
                    className="h-full w-full object-cover object-top"
                  />
                </div>
              </div>
              <div className="mt-4 rounded-lg border border-background-200 bg-background-50 p-4">
                <p className="text-xs text-foreground-500">站内热度</p>
                <p className="mt-1 flex items-center gap-1.5 font-heading text-xl font-bold text-primary-600">
                  <i className="ri-fire-line"></i>
                  {(drama.heat / 10000).toFixed(1)} 万
                </p>
                <p className="mt-3 text-xs leading-relaxed text-foreground-500">
                  热度综合播放、收藏、评论与分享行为，反映近期讨论度。
                </p>
              </div>
            </div>

            <div className="flex-1">
              <div className="flex flex-wrap gap-2">
                {drama.tags.map((tag) => (
                  <Link
                    key={tag}
                    to={`/dramas?cat=${encodeURIComponent(tag)}`}
                    className="rounded-full bg-secondary-100 px-3 py-1 text-xs text-secondary-900 transition-colors hover:bg-secondary-200"
                  >
                    {tag}
                  </Link>
                ))}
              </div>

              <h1 className="mt-4 font-heading text-3xl font-bold tracking-tight text-foreground-950 md:text-4xl">
                {drama.title}
              </h1>

              <div className="mt-4 flex flex-wrap items-center gap-4">
                <div className="flex items-center gap-2">
                  <RatingStars rating={drama.rating} size="text-base" />
                  <span className="font-heading text-2xl font-bold text-primary-600">
                    {drama.rating.toFixed(1)}
                  </span>
                  <span className="text-sm text-foreground-500">
                    （{drama.ratingCount.toLocaleString()} 人评分）
                  </span>
                </div>
                <span className="rounded-md bg-accent-100 px-2.5 py-1 text-xs text-accent-900">
                  {drama.status}
                </span>
              </div>

              <dl className="mt-7 grid grid-cols-2 gap-y-4 gap-x-6 border-y border-background-200 py-5 md:grid-cols-4">
                {[
                  { label: "导演", value: drama.director },
                  { label: "编剧", value: drama.screenwriter },
                  { label: "集数", value: `${drama.episodes} 集` },
                  { label: "地区 / 年份", value: `${drama.region} · ${drama.year}` },
                ].map((item) => (
                  <div key={item.label}>
                    <dt className="text-xs text-foreground-500">{item.label}</dt>
                    <dd className="mt-1.5 font-heading text-sm font-semibold text-foreground-950">
                      {item.value}
                    </dd>
                  </div>
                ))}
              </dl>

              <div className="mt-6">
                <h2 className="font-heading text-lg font-bold text-foreground-950">剧情简介</h2>
                <p className="mt-3 text-sm leading-relaxed text-foreground-600 md:text-base">
                  {drama.synopsis}
                </p>
              </div>

              <div className="mt-7 flex flex-wrap gap-3">
                <a
                  href="#cast"
                  className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm text-background-50 transition-colors hover:bg-primary-600"
                >
                  <i className="ri-team-line"></i>
                  查看完整演员表
                </a>
                <a
                  href="#behind"
                  className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 px-5 py-2.5 text-sm text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-600"
                >
                  <i className="ri-clapperboard-line"></i>
                  拍摄花絮
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      <section id="cast" className="mx-auto w-full max-w-content px-4 py-12 md:px-6 md:py-16 scroll-mt-24">
        <div className="flex items-end justify-between gap-4">
          <div>
            <h2 className="font-heading text-2xl font-bold tracking-tight text-foreground-950">
              演员表与角色
            </h2>
            <p className="mt-2 text-sm text-foreground-600">
              按出场顺序整理，点击演员姓名可查看其参演的全部短剧。
            </p>
          </div>
          <Link to="/cast" className="hidden whitespace-nowrap text-sm text-primary-600 hover:text-primary-700 sm:block">
            演员库 →
          </Link>
        </div>

        <div className="mt-7 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
          {drama.cast.map((member) => (
            <Link
              key={`${member.name}-${member.role}`}
              to={`/cast?q=${encodeURIComponent(member.name)}`}
              className="group flex items-center gap-3 rounded-lg border border-background-200 bg-background-50 p-3 transition-colors hover:border-primary-300"
            >
              <div className="h-14 w-14 shrink-0 overflow-hidden rounded-md bg-background-200">
                <img
                  src={member.avatar}
                  alt={`演员${member.name}头像`}
                  title={`${member.name} ${drama.title} 演员表`}
                  className="h-full w-full object-cover object-top"
                />
              </div>
              <div className="min-w-0">
                <p className="truncate font-heading text-sm font-semibold text-foreground-950 transition-colors group-hover:text-primary-600">
                  {member.name}
                </p>
                <p className="mt-0.5 truncate text-xs text-foreground-500">饰 {member.role}</p>
              </div>
            </Link>
          ))}
        </div>
      </section>

      <section className="border-y border-background-200 bg-background-100">
        <div className="mx-auto w-full max-w-content px-4 py-12 md:px-6 md:py-16">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-12">
            <div className="lg:col-span-7">
              <h2 className="font-heading text-2xl font-bold tracking-tight text-foreground-950">
                主创阵容与制作信息
              </h2>
              <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
                {[
                  { label: "导演", value: drama.director, icon: "ri-movie-line" },
                  { label: "编剧", value: drama.screenwriter, icon: "ri-quill-pen-line" },
                  { label: "出品方", value: drama.producer, icon: "ri-building-4-line" },
                ].map((item) => (
                  <div key={item.label} className="rounded-lg border border-background-200 bg-background-50 p-5">
                    <span className="flex h-10 w-10 items-center justify-center rounded-md bg-primary-50 text-primary-600">
                      <i className={`${item.icon} text-lg`}></i>
                    </span>
                    <p className="mt-4 text-xs text-foreground-500">{item.label}</p>
                    <p className="mt-1 font-heading text-sm font-semibold text-foreground-950">
                      {item.value}
                    </p>
                  </div>
                ))}
              </div>

              <div id="behind" className="mt-10 scroll-mt-24">
                <h2 className="font-heading text-2xl font-bold tracking-tight text-foreground-950">
                  拍摄花絮与幕后制作故事
                </h2>
                <ol className="mt-6 flex flex-col gap-4">
                  {drama.behindScenes.map((note, index) => (
                    <li
                      key={note}
                      className="flex gap-4 rounded-lg border border-background-200 bg-background-50 p-4 md:p-5"
                    >
                      <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-accent-500 font-heading text-xs font-bold text-secondary-950">
                        {index + 1}
                      </span>
                      <p className="text-sm leading-relaxed text-foreground-700">{note}</p>
                    </li>
                  ))}
                </ol>
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
                <h2 className="font-heading text-lg font-bold text-foreground-950">评分分布</h2>
                <div className="mt-5 flex flex-col gap-3">
                  {distribution.map((pct, index) => {
                    const star = 5 - index;
                    return (
                      <div key={star} className="flex items-center gap-3">
                        <span className="w-10 shrink-0 text-xs text-foreground-600">{star} 星</span>
                        <div className="h-2 flex-1 overflow-hidden rounded-full bg-background-200">
                          <div
                            className="h-full rounded-full bg-accent-500"
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                        <span className="w-9 shrink-0 text-right text-xs text-foreground-500">
                          {pct}%
                        </span>
                      </div>
                    );
                  })}
                </div>
                <p className="mt-5 border-t border-background-200 pt-4 text-xs leading-relaxed text-foreground-500">
                  评分仅统计看完至少三分之一剧集的观众，已剔除异常刷分数据。
                </p>
              </div>

              {relatedNews.length > 0 && (
                <div className="mt-5 rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
                  <h2 className="font-heading text-lg font-bold text-foreground-950">相关爆料</h2>
                  <ul className="mt-4 flex flex-col divide-y divide-background-200">
                    {relatedNews.map((article) => (
                      <li key={article.id} className="py-3 first:pt-0 last:pb-0">
                        <Link
                          to={`/news/${article.id}`}
                          className="text-sm leading-relaxed text-foreground-800 hover:text-primary-600"
                        >
                          {article.title}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {related.length > 0 && (
        <section className="mx-auto w-full max-w-content px-4 py-12 md:px-6 md:py-16">
          <h2 className="font-heading text-2xl font-bold tracking-tight text-foreground-950">
            看过这部的人还在追
          </h2>
          <div className="mt-7 grid grid-cols-2 gap-4 md:grid-cols-4 md:gap-5">
            {related.map((item) => (
              <DramaCard key={item.id} drama={item} />
            ))}
          </div>
        </section>
      )}
    </SiteLayout>
  );
}